import { Button, Chip, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useFinancials } from './editor';

import { MaterialSymbol } from '@/components/MaterialSymbol';
import { SECTION_HEADER_SX } from '@/components/ui';

/**
 * The Financials page's section header — the counterpart to `PlanPanel`'s
 * "Project plan · N tasks · [Add task]" row, which this page never had.
 *
 * Without it the populated page went straight from the project title to the
 * two panels, and the word "Contracts" appeared nowhere on screen: the rail's
 * `MicroLabel` is gated on `open` and the rail lands shut, so the only label
 * naming the thing rendered once you had already found it. The first pilot
 * test hit exactly that — "the contracts menu was hard to find".
 *
 * The title stays "Project budget", matching the zero-contract branch in
 * `FinancialsPage`: this page is the budget, and the contracts are how it is
 * made. The count is what tells you the rail has something in it.
 *
 * `financials-meta` is deliberately the same automation id the empty branch
 * uses for its "No contract" chip — one id answers "how many contracts does
 * this page have" in both states. A test that wants the empty wording must
 * assert the text, not just the id.
 */
export const ContractsSectionHeader = () => {
  const { effectiveContracts, actions } = useFinancials();
  const count = effectiveContracts.length;
  const { t } = useTranslation(['pmFinancials', 'pmCommon']);

  return (
    <Stack direction="row" sx={SECTION_HEADER_SX}>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
        {/* h4, matching PlanPanel: the project title above owns the 20px level. */}
        <Typography variant="h4" component="h2">
          {t('pmCommon:term.ProjectBudget')}
        </Typography>
        <Chip
          label={t('contracts.Count', { count })}
          size="small"
          color="fog"
          data-automation-id="financials-meta"
        />
      </Stack>
      {/*
        `add-contract-button`, mirroring Planning's `add-task-button` — and NOT
        `add-contract`, which the rail's own row owns; both are on screen at
        once when the rail is open. The zero-contract branch deliberately has
        no header button (its card is the one call to action), which
        `financials-contracts-crud.spec.ts` asserts by this id — an assertion
        that passed vacuously until something finally used it.
      */}
      <Button
        variant="contained"
        startIcon={<MaterialSymbol name="add" size={16} />}
        data-automation-id="add-contract-button"
        onClick={() => void actions.createContract()}
      >
        {t('actions.AddContract')}
      </Button>
    </Stack>
  );
};
